import type {
  NotificationPublic,
  QuoteDispositionKind,
  QuoteExportFormat,
  QuoteKind,
  QuoteTaskStatus,
  SquawkStatus,
  TaskReviewDecision,
  Workscope,
} from "@common/client"
import { sanitizePostHogRoute } from "@common/lib/posthog"
import { usePostHog } from "@posthog/react"
import { useCallback, useEffect, useRef } from "react"

export type ActionSource =
  | "agent_card"
  | "detail_pane"
  | "row_action"
  | "bulk_action"
  | "notification"

type CreationMethod =
  | "manual"
  | "agent_file_upload"
  | "agent_chat"
  | "squawk_new_quote"
  | "squawk_existing_quote"
  | "duplicate"

type ScopeEventContext = {
  tenant_id: string
}

export type TrackingEventProperties = {
  "404_page_viewed": {
    path: string
    referrer?: string
    from_trace_id?: string
  }
  notification_opened: {
    type: NotificationPublic["type"]
  }
  email_shared_with_team: {
    email_id: string
  }
  quote_created: {
    quote_id: string
    creation_method: CreationMethod
    quote_kind?: QuoteKind
  }
  quote_opened: {
    quote_id: string
    quote_kind: QuoteKind
    quote_disposition: QuoteDispositionKind
    work_package_id?: string
  }
  quote_tab_opened: {
    quote_id: string
    tab: string
  }
  quote_task_opened: {
    quote_id: string
    quote_task_id: string
    view_instance_id: string
  }
  quote_task_closed: {
    quote_id: string
    quote_task_id: string
    view_instance_id: string
    duration_ms: number
    active_duration_ms: number
    exit_reason: "unmounted"
  }
  quote_task_added: {
    quote_id: string
    quote_task_id?: string
  }
  quote_task_removed: {
    quote_id: string
    quote_task_id: string
  }
  quote_task_edited: {
    quote_id: string
    quote_task_id: string
    fields_changed: string[]
  }
  quote_task_accepted: {
    quote_id: string
    quote_task_id: string
    from_status: QuoteTaskStatus
    to_status: QuoteTaskStatus
  }
  quote_task_excluded: {
    quote_id: string
    quote_task_id: string
    from_status: QuoteTaskStatus
  }
  quote_task_included: {
    quote_id: string
    quote_task_id: string
    from_status: QuoteTaskStatus
    to_status: QuoteTaskStatus
  }
  quote_task_review_decided: {
    quote_id: string
    quote_task_id: string
    decision: TaskReviewDecision
    has_comment: boolean
    is_bulk: boolean
  }
  quote_review_requested: {
    quote_id: string
    reviewer_count: number
  }
  quote_review_returned: {
    quote_id: string
  }
  quote_send_opened: {
    quote_id: string
    task_count: number
    is_resend: boolean
  }
  quote_preview_opened: {
    quote_id: string
  }
  quote_sent_to_customer: {
    quote_id: string
    delivery: "email" | "manual"
    recipient_count: number
    cc_count: number
    task_count: number
    is_resend: boolean
  }
  quote_customer_email_resent: {
    quote_id: string
    recipient_count: number
  }
  quote_exported: {
    quote_id: string
    format: QuoteExportFormat
  }
  quote_edited: {
    quote_id: string
    fields_changed: string[]
  }
  squawk_status_changed: {
    squawk_id: string
    from_status: SquawkStatus
    to_status: SquawkStatus
  }
  squawk_edited: {
    squawk_id: string
    fields_changed: string[]
    action_source: ActionSource
  }
  squawk_draft_rendered: {
    trace_id: string
    squawk_count: number
  }
  squawk_logged: {
    squawk_id: string
    from_status: SquawkStatus
    to_status: SquawkStatus
    action_source: ActionSource
  }
  squawk_opened: {
    squawk_id: string
    view_instance_id: string
    action_source: ActionSource
  }
  squawk_closed: {
    squawk_id: string
    view_instance_id: string
    action_source: ActionSource
    duration_ms: number
    active_duration_ms: number
    exit_reason: "unmounted"
  }
  squawk_comment_added: {
    squawk_id: string
    action_source: ActionSource
  }
  squawk_photo_added: {
    squawk_id: string
    attachment_count: number
    action_source: ActionSource
  }
  squawk_review_decided: {
    squawk_id: string
    decision: "approved" | "rejected"
    from_status: SquawkStatus
    to_status: SquawkStatus
    action_source: ActionSource
  }
  squawk_sent_back: {
    squawk_id: string
    from_status: SquawkStatus
    to_status: SquawkStatus
    action_source: ActionSource
  }
  squawk_verified: {
    squawk_id: string
    from_status: SquawkStatus
    to_status: SquawkStatus
    action_source: ActionSource
  }
  squawk_reopened: {
    squawk_id: string
    from_status: SquawkStatus
    to_status: SquawkStatus
    action_source: ActionSource
  }
  squawk_added_to_quote: {
    quote_id: string
    squawk_count: number
    squawk_ids: string[]
    creation_method: "squawk_new_quote" | "squawk_existing_quote"
    action_source: ActionSource
  }
  squawk_customer_reply_sent: {
    squawk_id: string
    quote_id: string
    action_source: ActionSource
  }
  squawk_deleted: {
    squawk_id: string
    action_source: ActionSource
  }
  squawk_restored: {
    squawk_id: string
    action_source: ActionSource
  }
  canned_package_created: {
    package_id: string
    task_count: number
  }
  agent_session_started: {
    trace_id: string
    entry_point: "composer" | "attachment" | "connection"
    work_task_id?: string
  }
  agent_session_resumed: {
    trace_id: string
    entry_point: "history" | "deep_link"
  }
  agent_message_sent: {
    trace_id: string
    is_first_message: boolean
    attachment_count: number
    input_method: "text"
  }
  agent_response_completed: {
    trace_id: string
    latency_ms: number
    tool_count: number
    card_count: number
  }
  agent_response_failed: {
    trace_id?: string
    stage: "send"
  }
  agent_response_stopped: {
    trace_id: string
  }
  agent_card_rendered: {
    trace_id: string
    card_count: number
  }
  agent_question_answered: {
    trace_id: string
    question_count: number
    answered_count: number
    skipped_count: number
  }
  trace_message_error: Record<string, never>
  trace_attachment_uploaded: Record<string, never>
  trace_attachment_error: Record<string, never>
  trace_attachment_existing_attached: Record<string, never>
  trace_attachment_existing_error: Record<string, never>
  compliance_note_viewed: Record<string, never>
  suggestion_viewed: Record<string, never>
  compliance_note_sent_to_erp: Record<string, never>
  suggestion_sent_to_erp: Record<string, never>
  suggestion_option_changed: Record<string, never>
  scope_link_opened: ScopeEventContext & {
    quote_id: string
    revision_id: string
    revision_number: number
    quote_kind: QuoteKind
  }
  scope_access_code_requested: ScopeEventContext & {
    quote_id: string
    revision_id: string
  }
  scope_access_verified: ScopeEventContext & {
    quote_id: string
    revision_id: string
  }
  scope_opened: ScopeEventContext & {
    quote_id: string
    revision_id: string
    revision_number: number
    quote_kind: QuoteKind
    task_count: number
  }
  scope_tab_opened: ScopeEventContext & {
    quote_id: string
    revision_id: string
    tab: string
  }
  scope_task_opened: ScopeEventContext & {
    quote_id: string
    revision_id: string
    task_id: string
    view_instance_id: string
  }
  scope_task_closed: ScopeEventContext & {
    quote_id: string
    revision_id: string
    task_id: string
    view_instance_id: string
    duration_ms: number
    active_duration_ms: number
    exit_reason: "unmounted"
  }
  scope_task_accepted: ScopeEventContext & {
    quote_id: string
    revision_id: string
    task_id: string
    is_bulk: boolean
  }
  scope_task_rejected: ScopeEventContext & {
    quote_id: string
    revision_id: string
    task_id: string
    is_bulk: boolean
  }
  scope_task_decision_undone: ScopeEventContext & {
    quote_id: string
    revision_id: string
    task_id: string
  }
  scope_task_question_asked: ScopeEventContext & {
    quote_id: string
    revision_id: string
    task_id: string
  }
  scope_all_accepted: ScopeEventContext & {
    quote_id: string
    revision_id: string
    task_count: number
  }
  scope_workscope_awarded: ScopeEventContext & {
    quote_id: string
    revision_id: string
    task_id: string
    workscope: Workscope
  }
  scope_sign_opened: ScopeEventContext & {
    quote_id: string
    revision_id: string
    signed_task_count: number
  }
  scope_terms_opened: ScopeEventContext & {
    quote_id: string
    revision_id: string
  }
  scope_signed: ScopeEventContext & {
    quote_id: string
    revision_id: string
    signed_task_count: number
  }
  scope_approver_switched: ScopeEventContext & {
    quote_id: string
    revision_id: string
  }
}

export type TrackingEvent = keyof TrackingEventProperties

export type TrackEvent = <Event extends TrackingEvent>(
  event: Event,
  properties: TrackingEventProperties[Event],
) => void

export function useTrackEvent(): TrackEvent {
  const posthog = usePostHog()

  return useCallback(
    <Event extends TrackingEvent>(
      event: Event,
      properties: TrackingEventProperties[Event],
    ) =>
      posthog.capture(event, {
        event_version: 1,
        module: event.split("_")[0],
        route: sanitizePostHogRoute(window.location.pathname),
        surface: event.startsWith("scope_")
          ? "customer_portal"
          : event.startsWith("agent_") || event.startsWith("trace_")
            ? "agent"
            : "web_app",
        ...properties,
      }),
    [posthog],
  )
}

type TrackedView =
  | { kind: "squawk"; squawk_id: string }
  | { kind: "quote_task"; quote_id: string; quote_task_id: string }
  | {
      kind: "scope_task"
      tenant_id: string
      quote_id: string
      revision_id: string
      task_id: string
    }

export function useTrackView(view: TrackedView | null): void {
  const trackEvent = useTrackEvent()
  const viewRef = useRef(view)
  viewRef.current = view
  const viewKey =
    view === null
      ? null
      : view.kind === "squawk"
        ? view.squawk_id
        : view.kind === "quote_task"
          ? `${view.quote_id}:${view.quote_task_id}`
          : `${view.quote_id}:${view.revision_id}:${view.task_id}`

  useEffect(() => {
    if (viewKey === null) return
    const trackedView = viewRef.current
    if (trackedView === null) return
    const viewInstanceId = crypto.randomUUID()
    const startedAt = performance.now()
    let activeStartedAt = document.hidden ? null : startedAt
    let activeDuration = 0
    const visibilityChanged = () => {
      const now = performance.now()
      if (document.hidden && activeStartedAt !== null) {
        activeDuration += now - activeStartedAt
        activeStartedAt = null
      } else if (!document.hidden && activeStartedAt === null) {
        activeStartedAt = now
      }
    }
    document.addEventListener("visibilitychange", visibilityChanged)

    switch (trackedView.kind) {
      case "squawk":
        trackEvent("squawk_opened", {
          squawk_id: trackedView.squawk_id,
          view_instance_id: viewInstanceId,
          action_source: "detail_pane",
        })
        break
      case "quote_task":
        trackEvent("quote_task_opened", {
          quote_id: trackedView.quote_id,
          quote_task_id: trackedView.quote_task_id,
          view_instance_id: viewInstanceId,
        })
        break
      case "scope_task":
        trackEvent("scope_task_opened", {
          tenant_id: trackedView.tenant_id,
          quote_id: trackedView.quote_id,
          revision_id: trackedView.revision_id,
          task_id: trackedView.task_id,
          view_instance_id: viewInstanceId,
        })
        break
    }

    return () => {
      const endedAt = performance.now()
      if (activeStartedAt !== null) activeDuration += endedAt - activeStartedAt
      document.removeEventListener("visibilitychange", visibilityChanged)
      const timing = {
        view_instance_id: viewInstanceId,
        duration_ms: Math.round(endedAt - startedAt),
        active_duration_ms: Math.round(activeDuration),
        exit_reason: "unmounted" as const,
      }
      switch (trackedView.kind) {
        case "squawk":
          trackEvent("squawk_closed", {
            ...timing,
            squawk_id: trackedView.squawk_id,
            action_source: "detail_pane",
          })
          break
        case "quote_task":
          trackEvent("quote_task_closed", {
            ...timing,
            quote_id: trackedView.quote_id,
            quote_task_id: trackedView.quote_task_id,
          })
          break
        case "scope_task":
          trackEvent("scope_task_closed", {
            ...timing,
            tenant_id: trackedView.tenant_id,
            quote_id: trackedView.quote_id,
            revision_id: trackedView.revision_id,
            task_id: trackedView.task_id,
          })
          break
      }
    }
  }, [trackEvent, viewKey])
}
